import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  PenTool,
  Puzzle,
  Users,
  Trophy,
  Star,
  Sparkles,
  GraduationCap,
  Brain,
  Calendar,
  BarChart,
  Target,
  CheckCircle2,
  ArrowRight,
  UserCheck,
  ClipboardList,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import GradeSelection from '@/components/home/GradeSelection';
import Seo from '@/components/seo/Seo';
import StructuredData from '@/components/seo/StructuredData';

const Index = () => {
  const navigate = useNavigate();

  const audiences = [
    {
      title: 'Students',
      description: 'Take AI tests, do worksheets, practice writing, and play learning games.',
      icon: GraduationCap,
      cta: 'Student sign in',
      to: '/auth',
      accent: 'from-blue-500/15 to-blue-500/0 border-blue-500/30',
      iconBg: 'bg-blue-500/15 text-blue-600',
    },
    {
      title: 'Parents',
      description: 'Connect with your kids, follow their progress, and view tutoring schedules.',
      icon: Users,
      cta: 'Parent portal',
      to: '/parent/auth',
      accent: 'from-purple-500/15 to-purple-500/0 border-purple-500/30',
      iconBg: 'bg-purple-500/15 text-purple-600',
    },
    {
      title: 'Tutors & Instructors',
      description: 'Create AI-generated tests, schedule sessions, and track student outcomes.',
      icon: ClipboardList,
      cta: 'Instructor sign in',
      to: '/auth',
      accent: 'from-emerald-500/15 to-emerald-500/0 border-emerald-500/30',
      iconBg: 'bg-emerald-500/15 text-emerald-600',
    },
  ];

  const capabilities = [
    { icon: Brain, title: 'AI-Generated Tests', desc: 'Fresh, grade-adaptive questions every time.' },
    { icon: Puzzle, title: 'Interactive Worksheets', desc: 'Puzzles, matching, and sorting activities.' },
    { icon: PenTool, title: 'Writing Pad', desc: 'Guided prompts with AI feedback and suggestions.' },
    { icon: Calendar, title: 'Tutoring Scheduling', desc: 'Plan sessions with a shared family calendar.' },
    { icon: UserCheck, title: 'Parent–Child Connection', desc: 'Invite your child and stay in the loop.' },
    { icon: BarChart, title: 'Progress Analytics', desc: 'Track results, strengths, and gaps over time.' },
  ];

  const steps = [
    { n: 1, title: 'Sign up', desc: 'Join as a student, parent, or tutor.' },
    { n: 2, title: 'Connect', desc: 'Link your family or class in a few clicks.' },
    { n: 3, title: 'Learn & track', desc: 'Practice, schedule, and watch progress grow.' },
  ];

  const faqs = [
    { q: 'What is Grade Test Pro?', a: 'Grade Test Pro is an AI-powered learning, tutoring, and family platform for students in grades 1–9. It includes practice tests, worksheets, a writing pad, tutoring scheduling, and a parent portal.' },
    { q: 'Which grades are supported?', a: 'Grade Test Pro supports students from grade 1 through grade 9, with grade-adaptive content for math, reading, and writing.' },
    { q: 'Is there a parent portal?', a: 'Yes. Parents can connect with their child, view tutoring schedules, monitor assigned tests, and track academic progress.' },
    { q: 'How does tutoring scheduling work?', a: 'Tutors create one-time or recurring sessions. Students and parents see them in a shared calendar with reminders.' },
    { q: 'Is Grade Test Pro free to use?', a: 'You can sign up and start practicing for free. Some advanced tutoring and organization features are available to instructors.' },
    { q: 'How are tests generated?', a: 'Questions are generated by AI using grade-appropriate curriculum guidelines, so each practice session is fresh.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative">
      <Seo
        title="Grade Test Pro — AI Learning & Tutoring for Grades 1–9"
        description="AI-powered tests, worksheets, writing practice, tutoring scheduling, and a parent portal — for students, parents, and tutors in grades 1–9."
        canonicalPath="/"
      />
      <StructuredData
        type="Organization"
        data={{
          name: 'Grade Test Pro',
          url: 'https://gradetestpro.ca',
          logo: 'https://n-micro.ca/images/gradetestpro.png',
          sameAs: ['https://eqao.n-micro.ca'],
        }}
      />
      <StructuredData
        type="WebSite"
        data={{
          name: 'Grade Test Pro',
          url: 'https://gradetestpro.ca',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://gradetestpro.ca/?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <StructuredData
        type="FAQPage"
        data={{
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <Header />

      <main className="relative z-10">
        {/* Hero Banner */}
        <section className="relative overflow-hidden bg-gradient-to-br from-background via-secondary to-background border-b border-border">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-accent-foreground/10 blur-3xl" />
          <div className="absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />

          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute text-primary/20"
                style={{ top: `${(i * 37) % 100}%`, left: `${(i * 53) % 100}%` }}
                animate={{ y: [0, -20, 0], rotate: [0, 12, 0] }}
                transition={{ duration: 8 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
              >
                {i % 4 === 0 && <GraduationCap className="h-7 w-7" />}
                {i % 4 === 1 && <Brain className="h-8 w-8" />}
                {i % 4 === 2 && <BookOpen className="h-6 w-6" />}
                {i % 4 === 3 && <Sparkles className="h-6 w-6" />}
              </motion.div>
            ))}
          </div>

          <div className="relative z-10 container mx-auto px-4 py-20 md:py-28 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full backdrop-blur-sm"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  AI-powered practice for students — connecting parents & tutors
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl md:text-7xl font-bold mb-6 leading-tight tracking-tight text-foreground"
              >
                Grade Test{" "}
                <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  Pro
                </span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mb-10 max-w-2xl mx-auto px-2"
              >
                <p className="text-lg sm:text-xl md:text-2xl font-medium text-foreground leading-snug mb-4">
                  Learn smarter. <span className="text-primary">Stay connected.</span> Grow together.
                </p>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
                  {['AI tests', 'Worksheets', 'Tutoring', 'Parent portal'].map((t) => (
                    <span key={t} className="inline-flex items-center rounded-full border border-border bg-background/60 backdrop-blur px-3 py-1 text-xs sm:text-sm font-medium text-foreground">
                      {t}
                    </span>
                  ))}
                  <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs sm:text-sm font-semibold text-primary">
                    Grades 1–9
                  </span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row justify-center gap-4 mb-12"
              >
                <Button
                  onClick={() => navigate("/chat")}
                  size="lg"
                  className="text-base font-semibold px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Chat with AI Tutor
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/grade")}
                  className="text-base font-semibold px-8 py-6 rounded-full border-2"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  Browse Practice
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate("/auth")}
                  className="text-base font-semibold px-8 py-6 rounded-full"
                >
                  <Users className="h-5 w-5 mr-2" />
                  Sign In
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Audience Picker */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Built for everyone in learning</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Whether you're studying, supporting your child, or teaching — Grade Test Pro has a place for you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className={`h-full bg-gradient-to-b ${a.accent} border-2 hover:shadow-xl transition-all rounded-2xl`}>
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className={`w-14 h-14 rounded-2xl ${a.iconBg} flex items-center justify-center mb-5`}>
                      <a.icon className="h-7 w-7" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-foreground">{a.title}</h3>
                    <p className="text-muted-foreground mb-6 flex-1">{a.description}</p>
                    <Button onClick={() => navigate(a.to)} variant="outline" className="rounded-full self-start">
                      {a.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Everything in one place</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From AI-powered practice to scheduling and family connection.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all rounded-2xl">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                      <c.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-foreground">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">{c.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">How it works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get up and running in three simple steps.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="h-full rounded-2xl border-2 border-dashed border-primary/20 bg-background/60">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                      {s.n}
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-foreground">{s.title}</h3>
                    <p className="text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Grade Selection Section */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Choose Your Grade</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Select your grade level to access age-appropriate content and activities.
            </p>
          </motion.div>

          <GradeSelection />
        </section>

        {/* Parent Portal Band */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white overflow-hidden relative rounded-3xl border-0">
              <div className="absolute inset-0 opacity-10">
                <Users className="h-full w-full" />
              </div>
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <Users className="h-14 w-14 mb-4" />
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">For Parents</h2>
                    <p className="text-base md:text-lg text-blue-50 mb-6">
                      Stay connected with your child's learning journey. Monitor tutoring schedules,
                      track results, and view assigned activities — all in one place.
                    </p>
                    <ul className="space-y-3 text-blue-50 mb-6">
                      <li className="flex items-center"><Calendar className="h-5 w-5 mr-2" />View all tutoring sessions</li>
                      <li className="flex items-center"><BarChart className="h-5 w-5 mr-2" />Track academic progress</li>
                      <li className="flex items-center"><Target className="h-5 w-5 mr-2" />Monitor assigned tests</li>
                    </ul>
                  </div>
                  <div className="flex justify-center md:justify-end">
                    <Button
                      size="lg"
                      className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 shadow-2xl rounded-full"
                      onClick={() => navigate('/parent/auth')}
                    >
                      Open Parent Portal
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Tutor Band */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white overflow-hidden relative rounded-3xl border-0">
              <div className="absolute inset-0 opacity-10">
                <ClipboardList className="h-full w-full" />
              </div>
              <CardContent className="p-8 md:p-12 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="md:order-2">
                    <ClipboardList className="h-14 w-14 mb-4" />
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">For Tutors & Instructors</h2>
                    <p className="text-base md:text-lg text-emerald-50 mb-6">
                      Run your tutoring practice end-to-end — generate tests, schedule sessions,
                      and analyze student performance.
                    </p>
                    <ul className="space-y-3 text-emerald-50 mb-6">
                      <li className="flex items-center"><Brain className="h-5 w-5 mr-2" />AI question generation</li>
                      <li className="flex items-center"><Calendar className="h-5 w-5 mr-2" />Recurring session scheduling</li>
                      <li className="flex items-center"><BarChart className="h-5 w-5 mr-2" />Student outcome analytics</li>
                    </ul>
                  </div>
                  <div className="flex justify-center md:justify-start md:order-1">
                    <Button
                      size="lg"
                      className="bg-white text-emerald-700 hover:bg-emerald-50 text-lg px-8 py-6 shadow-2xl rounded-full"
                      onClick={() => navigate('/auth')}
                    >
                      Get started as a tutor
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Stats / Trust Band */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-3xl border-0">
              <CardContent className="p-10 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
                  <div>
                    <Brain className="h-12 w-12 mx-auto mb-4" />
                    <div className="text-3xl md:text-4xl font-bold">AI-Powered</div>
                    <div className="text-blue-100 text-base md:text-lg">Dynamic Content</div>
                  </div>
                  <div>
                    <Trophy className="h-12 w-12 mx-auto mb-4" />
                    <div className="text-3xl md:text-4xl font-bold">Fresh</div>
                    <div className="text-blue-100 text-base md:text-lg">Every Time</div>
                  </div>
                  <div>
                    <Star className="h-12 w-12 mx-auto mb-4" />
                    <div className="text-3xl md:text-4xl font-bold">Grade-Adaptive</div>
                    <div className="text-blue-100 text-base md:text-lg">Learning System</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Final CTA */}
        <section className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto"
          >
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Bring learning, tutoring, and family together
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              One platform for students, parents, and tutors. Get started in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="rounded-full px-8" onClick={() => navigate('/auth')}>
                Start free
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-8 border-2" onClick={() => navigate('/parent/auth')}>
                Parent portal
              </Button>
              <Button size="lg" variant="ghost" className="rounded-full px-8" onClick={() => navigate('/auth')}>
                Tutor sign in
              </Button>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
